import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Code,
  Palette,
  Activity,
  Copy,
  Check,
  ExternalLink,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  Settings,
  Image,
  Type,
  Lock,
  Sparkles,
  Play,
  FileText,
  User,
  Webhook,
} from "lucide-react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import api from "@/services/api";
import { useAuthStore } from "@/store/auth.store";

const getCurrentBackend = () => ({
  nodejs: "Node.js",
  python: "Python",
  go: "Go",
  rust: "Rust",
  php: "PHP",
  ruby: "Ruby",
  csharp: "C#",
});

const getCurrentFrontend = () => ({
  vanilla: "Vanilla JS",
  react: "React",
  vue: "Vue 3",
  angular: "Angular",
  nextjs: "Next.js",
  svelte: "Svelte",
});

// Color picker component
const ColorPicker = ({ label, value, onChange, id }) => {
  const inputId = id || `color-${label.replace(/\s+/g, "-").toLowerCase()}`;
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex gap-2">
        <div
          className="w-10 h-10 rounded border cursor-pointer"
          style={{ backgroundColor: value || "#3b82f6" }}
          onClick={() => document.getElementById(inputId)?.click()}
        />
        <Input
          id={inputId}
          type="color"
          value={value || "#3b82f6"}
          onChange={(e) => onChange(e.target.value)}
          className="w-20 h-10 p-1 cursor-pointer"
        />
        <Input
          type="text"
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder="#3b82f6"
          className="flex-1"
        />
      </div>
    </div>
  );
};

// Theme preview component
const ThemePreview = ({ theme }) => (
  <div
    className="border rounded-lg overflow-hidden"
    style={{
      backgroundColor: theme.backgroundColor || "#ffffff",
      fontFamily: theme.fontFamily || "Inter, sans-serif",
    }}
  >
    {/* Header */}
    <div
      className="p-4 flex items-center gap-3"
      style={{
        backgroundColor: theme.headerColor || theme.primaryColor || "#3b82f6",
      }}
    >
      {theme.logoUrl ? (
        <img
          src={theme.logoUrl}
          alt=""
          role="presentation"
          className="h-8"
          onError={(e) => (e.target.style.display = "none")}
        />
      ) : (
        <div className="w-8 h-8 bg-white/20 rounded" />
      )}
      <span
        className="font-semibold"
        style={{ color: theme.buttonTextColor || "#ffffff" }}
      >
        {theme.companyName || "Your Company"}
      </span>
    </div>

    {/* Content */}
    <div className="p-4 space-y-4">
      <h3
        className="font-semibold text-lg"
        style={{ color: theme.textColor || "#1f2937" }}
      >
        Sign Document
      </h3>
      <p
        className="text-sm"
        style={{
          color: theme.textColor ? `${theme.textColor}99` : "#6b7280",
        }}
      >
        Please review and sign the following document.
      </p>

      {/* Sample button */}
      <button
        className="px-4 py-2 rounded font-medium"
        style={{
          backgroundColor: theme.primaryColor || "#3b82f6",
          color: theme.buttonTextColor || "#ffffff",
        }}
      >
        Sign Now
      </button>

      {/* Powered by */}
      {!theme.hideeScribeBranding && (
        <p className="text-xs text-gray-400 dark:text-gray-500 pt-4">
          Powered by eScribe
        </p>
      )}
    </div>
  </div>
);

// Session status badge
const SessionStatusBadge = ({ status }) => {
  const config = {
    pending: { icon: Clock, className: "bg-amber-500 text-white" },
    active: { icon: Activity, className: "bg-blue-500 text-white" },
    completed: { icon: CheckCircle, className: "bg-emerald-500 text-white" },
    expired: { icon: XCircle, className: "bg-gray-500 text-white" },
    cancelled: { icon: XCircle, className: "bg-red-500 text-white" },
  };

  const { icon: Icon, className } = config[status] || config.pending;

  return (
    <Badge className={cn("gap-1", className)}>
      <Icon className="h-3 w-3" />
      {status}
    </Badge>
  );
};

// Code block component with syntax highlighting colors
const CodeBlock = ({ code, language, onCopy, copied }) => (
  <div className="relative">
    <div className="absolute top-2 right-2 flex items-center gap-2">
      <Badge
        variant="outline"
        className="text-xs bg-slate-800 text-slate-300 border-slate-600"
      >
        {language}
      </Badge>
      <Button
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-slate-400 hover:text-white hover:bg-slate-700"
        onClick={onCopy}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </Button>
    </div>
    <pre className="p-4 pt-12 bg-slate-900 text-slate-50 rounded-lg overflow-x-auto text-xs leading-relaxed">
      <code>{code}</code>
    </pre>
  </div>
);

// ============================================
// UpgradeRequired Component
// ============================================
const UpgradeRequired = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">
          {t("embed.title", "Embed Integration")}
        </h1>
        <p className="text-muted-foreground">
          {t(
            "embed.description",
            "Integrate eScribe signing into your application",
          )}
        </p>
      </div>

      {/* Single Card with divider */}
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-16 text-center">
          <div className="flex h-20 w-20 items-center justify-center rounded-full bg-primary/10 mb-6">
            <Lock className="h-10 w-10 text-primary" />
          </div>
          <h2 className="text-2xl font-semibold mb-2">
            {t("upgrade.featureNotAvailable", "Feature Not Available")}
          </h2>
          <p className="text-muted-foreground mb-6 max-w-md">
            {t(
              "upgrade.embedUpgradeDesc",
              "The Embed SDK allows you to seamlessly integrate document signing directly into your web application with your own branding.",
            )}
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Button onClick={() => navigate("/settings/billing")}>
              <Sparkles className="h-4 w-4 mr-2" />
              {t("upgrade.upgradePlan", "Upgrade Plan")}
            </Button>
            <Button variant="outline" onClick={() => navigate("/envelopes")}>
              {t("common.backToEnvelopes", "Back to Envelopes")}
            </Button>
          </div>

          {/* Feature highlights */}
          <div className="mt-8 pt-8 border-t w-full max-w-lg">
            <div className="w-full flex items-center justify-center gap-2 mb-4">
              <Code className="h-3.5 w-3.5 text-primary" />
              <h3 className="text-sm font-medium text-muted-foreground">
                {t("embed.withEmbedYouCan", "With Embed Integration you can:")}
              </h3>
            </div>
            <ul className="text-sm text-muted-foreground space-y-1 flex items-center justify-center flex-col">
              <li>
                {t(
                  "embed.feature1",
                  "Embed signing experiences directly in your app",
                )}
              </li>
              <li>
                {t(
                  "embed.feature2",
                  "Customize branding with your company's colors and logo",
                )}
              </li>
              <li>
                {t("embed.feature3", "Track embed sessions and monitor usage")}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const Embed = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, accessToken } = useAuthStore();

  // Check if user has embed feature - must be explicitly true
  const hasEmbedFeature = user?.subscription?.features?.embed === true;

  // Feature flags - read from user object (these are per-user settings within the feature)
  const embedEnabled = user?.embedEnabled || false;
  const whiteLabelEnabled = user?.whiteLabelEnabled || false;
  const customCssEnabled = user?.customCssEnabled || false;

  // Theme form state (local, not query data)
  const [themeForm, setThemeForm] = useState({
    companyName: "",
    logoUrl: "",
    faviconUrl: "",
    primaryColor: "#3b82f6",
    secondaryColor: "#64748b",
    backgroundColor: "#ffffff",
    headerColor: "",
    textColor: "#1f2937",
    buttonTextColor: "#ffffff",
    fontFamily: "Inter, sans-serif",
    fontUrl: "",
    hideeScribeBranding: false,
    customCss: "",
    allowedDomains: "",
  });

  // Code examples state
  const [copiedCode, setCopiedCode] = useState(null);
  const [selectedBackendLang, setSelectedBackendLang] = useState("nodejs");
  const [selectedFrontendLang, setSelectedFrontendLang] = useState("vanilla");

  // Tab state
  const [activeTab, setActiveTab] = useState("quickstart");

  // Quick Test state
  const [selectedEnvelopeId, setSelectedEnvelopeId] = useState("");
  const [selectedRecipientId, setSelectedRecipientId] = useState("");
  const [testEmbedUrl, setTestEmbedUrl] = useState("");
  const [testLoading, setTestLoading] = useState(false);
  const [availableRecipients, setAvailableRecipients] = useState([]);

  // Query: Load theme (only if feature enabled)
  const {
    data: theme,
    isLoading: themeLoading,
    error: themeQueryError,
  } = useQuery({
    queryKey: ["embed", "theme"],
    queryFn: async () => {
      const response = await api.get("/embed/themes");
      const { data } = response;

      if (data.success && data.data) {
        // Update form with loaded theme
        setThemeForm({
          companyName: data.data.companyName || "",
          logoUrl: data.data.logoUrl || "",
          faviconUrl: data.data.faviconUrl || "",
          primaryColor: data.data.primaryColor || "#3b82f6",
          secondaryColor: data.data.secondaryColor || "#64748b",
          backgroundColor: data.data.backgroundColor || "#ffffff",
          headerColor: data.data.headerColor || "",
          textColor: data.data.textColor || "#1f2937",
          buttonTextColor: data.data.buttonTextColor || "#ffffff",
          fontFamily: data.data.fontFamily || "Inter, sans-serif",
          fontUrl: data.data.fontUrl || "",
          hideeScribeBranding: data.data.hideeScribeBranding || false,
          customCss: data.data.customCss || "",
          allowedDomains: (data.data.allowedDomains || []).join(", "),
        });
        return data.data;
      }
      return null;
    },
    enabled: !!accessToken && hasEmbedFeature, // Don't fetch if feature is disabled
    retry: false,
  });

  const themeError = themeQueryError?.message || null;

  // Query: Load sessions (only if feature enabled)
  const {
    data: sessionsData,
    isLoading: sessionsLoading,
    error: sessionsQueryError,
    refetch: refetchSessions,
    isFetching: sessionsRefetching,
  } = useQuery({
    queryKey: ["embed", "sessions"],
    queryFn: async () => {
      const response = await api.get("/embed/sessions", {
        params: { limit: 50 },
      });
      const { data } = response;

      if (data.success && Array.isArray(data.data)) {
        // Calculate stats
        const stats = data.data.reduce(
          (acc, s) => {
            acc.total++;
            acc[s.status] = (acc[s.status] || 0) + 1;
            return acc;
          },
          {
            total: 0,
            active: 0,
            completed: 0,
            expired: 0,
            pending: 0,
            cancelled: 0,
          },
        );

        return { sessions: data.data, stats };
      }
      return {
        sessions: [],
        stats: {
          total: 0,
          active: 0,
          completed: 0,
          expired: 0,
          pending: 0,
          cancelled: 0,
        },
      };
    },
    enabled: !!accessToken && hasEmbedFeature, // Don't fetch if feature is disabled
  });

  const sessions = sessionsData?.sessions || [];
  const sessionStats = sessionsData?.stats || {
    total: 0,
    active: 0,
    completed: 0,
    expired: 0,
    pending: 0,
    cancelled: 0,
  };
  const sessionsError = sessionsQueryError?.message || null;

  // Query: Load sent envelopes for Quick Test
  const { data: testEnvelopesData, isLoading: testEnvelopesLoading } = useQuery(
    {
      queryKey: ["embed", "test-envelopes"],
      queryFn: async () => {
        const response = await api.get("/envelopes", {
          params: { status: "sent", limit: 50 },
        });
        const { data } = response;
        return data.data?.envelopes || data.data || [];
      },
      enabled: !!accessToken && hasEmbedFeature && activeTab === "test",
    },
  );

  const testEnvelopes = testEnvelopesData || [];

  // Query: Load selected envelope details (to get recipients)
  const { data: selectedEnvelopeData, isLoading: selectedEnvelopeLoading } =
    useQuery({
      queryKey: ["embed", "test-envelope", selectedEnvelopeId],
      queryFn: async () => {
        const response = await api.get(`/envelopes/${selectedEnvelopeId}`);
        const { data } = response;

        // Set recipients state - filter out completed/declined
        if (data.data?.recipients) {
          const available = data.data.recipients.filter(
            (r) => r.status !== "completed" && r.status !== "declined",
          );
          setAvailableRecipients(available);
        }

        return data.data;
      },
      enabled: !!accessToken && !!selectedEnvelopeId,
    });

  // Create test embed session
  const createTestSession = async () => {
    if (!selectedEnvelopeId || !selectedRecipientId) {
      toast.error("Please select an envelope and recipient");
      return;
    }

    setTestLoading(true);
    setTestEmbedUrl("");

    try {
      // Use the test endpoint which accepts JWT auth
      const response = await api.post("/embed/sessions/test", {
        type: "sign",
        envelopeId: selectedEnvelopeId,
        recipientId: selectedRecipientId,
        returnUrl: `${window.location.origin}/embed?test=complete`,
      });

      const { data } = response;

      if (data.success && data.data?.url) {
        setTestEmbedUrl(data.data.url);
        toast.success("Test session created!");
        // Refresh sessions list
        refetchSessions();
      } else {
        throw new Error(data.error?.message || "Failed to create session");
      }
    } catch (error) {
      toast.error(error.response?.data?.error?.message || error.message);
    } finally {
      setTestLoading(false);
    }
  };

  // Reset test state when envelope changes
  const handleEnvelopeChange = (envelopeId) => {
    setSelectedEnvelopeId(envelopeId);
    setSelectedRecipientId("");
    setTestEmbedUrl("");
    setAvailableRecipients([]); // Clear recipients until new envelope loads
  };

  // Mutation: Save theme
  const saveThemeMutation = useMutation({
    mutationFn: (payload) => {
      if (theme?.id) {
        return api.put(`/embed/themes/${theme.id}`, payload);
      }
      return api.post("/embed/themes", payload);
    },
    onSuccess: () => {
      toast.success(t("embed.theme.saved"));
      queryClient.invalidateQueries({ queryKey: ["embed", "theme"] });
    },
    onError: (err) => {
      toast.error(err.response?.data?.error?.message || err.message);
    },
  });

  // Refresh sessions handler
  const refreshSessions = async () => {
    await refetchSessions();
    toast.success("Sessions refreshed");
  };

  // Save theme handler
  const handleSaveTheme = () => {
    const payload = {
      companyName: themeForm.companyName,
      logoUrl: themeForm.logoUrl,
      faviconUrl: themeForm.faviconUrl,
      primaryColor: themeForm.primaryColor,
      secondaryColor: themeForm.secondaryColor,
      backgroundColor: themeForm.backgroundColor,
      headerColor: themeForm.headerColor,
      textColor: themeForm.textColor,
      buttonTextColor: themeForm.buttonTextColor,
      fontFamily: themeForm.fontFamily,
      fontUrl: themeForm.fontUrl,
      hideeScribeBranding: themeForm.hideeScribeBranding,
      customCss: themeForm.customCss,
      allowedDomains: themeForm.allowedDomains
        .split(",")
        .map((d) => d.trim())
        .filter(Boolean),
    };

    saveThemeMutation.mutate(payload);
  };

  // Derived loading state for save button
  const themeSaving = saveThemeMutation.isPending;

  // Copy code to clipboard
  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success(t("toast.copiedToClipboard"));
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const renderTestEnvelopesPlaceholder = () => {
    if (testEnvelopesLoading) {
      return "Loading envelopes...";
    }
    return testEnvelopes.length === 0
      ? "No sent envelopes found"
      : "Select an envelope";
  };

  const renderAvailableParticipantsPlaceholder = () => {
    if (selectedEnvelopeLoading) {
      return "Loading recipients...";
    }
    return availableRecipients.length === 0
      ? "No signers pending"
      : "Select a recipient";
  };

  // ==============================================
  // CODE EXAMPLES - BACKEND
  // ==============================================

  const backendExamples = {
    nodejs: `// Node.js / Express
      const express = require('express');
      const app = express();

      app.post('/api/get-embed-session', async (req, res) => {
        const response = await fetch('https://api.escribe.io/api/v1/embed/signing-session', {
          method: 'POST',
          headers: {
            'X-API-Key': 'YOUR_API_KEY',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            // Identify the signer by the envelope + recipient IDs you got back
            // when you created/sent the envelope via the API.
            // (Alternatively, pass recipientToken: '<recipient signing token>'.)
            envelopeId: 'ENVELOPE_ID',
            recipientId: 'RECIPIENT_ID',
            returnUrl: 'https://yourapp.com/complete',
            cancelUrl: 'https://yourapp.com/cancel',
          }),
        });

        const { data } = await response.json();

        // Return only the token to the frontend (never expose API key!)
        res.json({ sessionToken: data.token, embedUrl: data.url });
      });`,

    python: `# Python / Flask
      from flask import Flask, jsonify, request
      import requests

      app = Flask(__name__)

      @app.route('/api/get-embed-session', methods=['POST'])
      def get_embed_session():
          response = requests.post(
              'https://api.escribe.io/api/v1/embed/signing-session',
              headers={
                  'X-API-Key': 'YOUR_API_KEY',
                  'Content-Type': 'application/json',
              },
              json={
                  # Identify the signer by the envelope + recipient IDs from
                  # creating/sending the envelope via the API (or pass
                  # recipientToken instead).
                  'envelopeId': 'ENVELOPE_ID',
                  'recipientId': 'RECIPIENT_ID',
                  'returnUrl': 'https://yourapp.com/complete',
                  'cancelUrl': 'https://yourapp.com/cancel',
              }
          )

          data = response.json()

          # Return only the token to the frontend (never expose API key!)
          return jsonify({
              'sessionToken': data['data']['token'],
              'embedUrl': data['data']['url']
          })`,

    go: `// Go / Gin
      package main

      import (
          "bytes"
          "encoding/json"
          "net/http"
          "github.com/gin-gonic/gin"
      )

      type EmbedRequest struct {
          EnvelopeId  string \`json:"envelopeId"\`
          RecipientId string \`json:"recipientId"\`
          ReturnUrl   string \`json:"returnUrl"\`
          CancelUrl   string \`json:"cancelUrl"\`
      }

      func getEmbedSession(c *gin.Context) {
          // Identify the signer by the envelope + recipient IDs from
          // creating/sending the envelope via the API (or pass
          // recipientToken instead).
          reqBody := EmbedRequest{
              EnvelopeId:  "ENVELOPE_ID",
              RecipientId: "RECIPIENT_ID",
              ReturnUrl:   "https://yourapp.com/complete",
              CancelUrl:   "https://yourapp.com/cancel",
          }

          jsonData, _ := json.Marshal(reqBody)

          req, _ := http.NewRequest("POST", "https://api.escribe.io/api/v1/embed/signing-session", bytes.NewBuffer(jsonData))
          req.Header.Set("X-API-Key", "YOUR_API_KEY")
          req.Header.Set("Content-Type", "application/json")

          client := &http.Client{}
          resp, _ := client.Do(req)
          defer resp.Body.Close()

          var result map[string]interface{}
          json.NewDecoder(resp.Body).Decode(&result)

          data := result["data"].(map[string]interface{})
          c.JSON(200, gin.H{
              "sessionToken": data["token"],
              "embedUrl":     data["url"],
          })
      }`,

    rust: `// Rust / Actix-web
      use actix_web::{post, web, HttpResponse, Responder};
      use reqwest::Client;
      use serde::{Deserialize, Serialize};
      use serde_json::json;

      #[derive(Serialize)]
      #[serde(rename_all = "camelCase")]
      struct EmbedRequest {
          envelope_id: String,
          recipient_id: String,
          return_url: String,
          cancel_url: String,
      }

      #[derive(Deserialize)]
      struct EmbedResponse {
          data: EmbedData,
      }

      #[derive(Deserialize)]
      struct EmbedData {
          token: String,
          url: String,
      }

      #[post("/api/get-embed-session")]
      async fn get_embed_session() -> impl Responder {
          let client = Client::new();

          // Identify the signer by the envelope + recipient IDs from
          // creating/sending the envelope via the API (or pass
          // recipientToken instead).
          let request_body = EmbedRequest {
              envelope_id: "ENVELOPE_ID".to_string(),
              recipient_id: "RECIPIENT_ID".to_string(),
              return_url: "https://yourapp.com/complete".to_string(),
              cancel_url: "https://yourapp.com/cancel".to_string(),
          };

          let response = client
              .post("https://api.escribe.io/api/v1/embed/signing-session")
              .header("X-API-Key", "YOUR_API_KEY")
              .json(&request_body)
              .send()
              .await
              .unwrap();

          let result: EmbedResponse = response.json().await.unwrap();

          HttpResponse::Ok().json(json!({
              "sessionToken": result.data.token,
              "embedUrl": result.data.url
          }))
      }`,

    php: `<?php
      // PHP / Laravel

      use Illuminate\\Http\\Request;
      use Illuminate\\Support\\Facades\\Http;
      use Illuminate\\Support\\Facades\\Route;

      Route::post('/api/get-embed-session', function (Request $request) {
          $response = Http::withHeaders([
              'X-API-Key' => env('ESCRIBE_API_KEY'),
              'Content-Type' => 'application/json',
          ])->post('https://api.escribe.io/api/v1/embed/signing-session', [
              // Identify the signer by the envelope + recipient IDs from
              // creating/sending the envelope via the API (or pass
              // recipientToken instead).
              'envelopeId' => 'ENVELOPE_ID',
              'recipientId' => 'RECIPIENT_ID',
              'returnUrl' => 'https://yourapp.com/complete',
              'cancelUrl' => 'https://yourapp.com/cancel',
          ]);

          $data = $response->json()['data'];

          // Return only the token to the frontend (never expose API key!)
          return response()->json([
              'sessionToken' => $data['token'],
              'embedUrl' => $data['url'],
          ]);
      });`,

    ruby: `# Ruby / Rails
      class EmbedController < ApplicationController
        def create_session
          response = HTTParty.post(
            'https://api.escribe.io/api/v1/embed/signing-session',
            headers: {
              'X-API-Key' => ENV['ESCRIBE_API_KEY'],
              'Content-Type' => 'application/json'
            },
            body: {
              # Identify the signer by the envelope + recipient IDs from
              # creating/sending the envelope via the API (or pass
              # recipientToken instead).
              envelopeId: 'ENVELOPE_ID',
              recipientId: 'RECIPIENT_ID',
              returnUrl: 'https://yourapp.com/complete',
              cancelUrl: 'https://yourapp.com/cancel'
            }.to_json
          )

          data = JSON.parse(response.body)['data']

          # Return only the token to the frontend (never expose API key!)
          render json: {
            sessionToken: data['token'],
            embedUrl: data['url']
          }
        end
      end`,

    csharp: `// C# / ASP.NET Core
      using Microsoft.AspNetCore.Mvc;
      using System.Net.Http;
      using System.Text;
      using System.Text.Json;

      [ApiController]
      [Route("api")]
      public class EmbedController : ControllerBase
      {
          private readonly HttpClient _httpClient;

          public EmbedController(IHttpClientFactory httpClientFactory)
          {
              _httpClient = httpClientFactory.CreateClient();
          }

          [HttpPost("get-embed-session")]
          public async Task<IActionResult> GetEmbedSession()
          {
              var request = new
              {
                  // Identify the signer by the envelope + recipient IDs from
                  // creating/sending the envelope via the API (or pass
                  // recipientToken instead).
                  envelopeId = "ENVELOPE_ID",
                  recipientId = "RECIPIENT_ID",
                  returnUrl = "https://yourapp.com/complete",
                  cancelUrl = "https://yourapp.com/cancel"
              };

              var content = new StringContent(
                  JsonSerializer.Serialize(request),
                  Encoding.UTF8,
                  "application/json"
              );

              _httpClient.DefaultRequestHeaders.Add("X-API-Key", "YOUR_API_KEY");

              var response = await _httpClient.PostAsync(
                  "https://api.escribe.io/api/v1/embed/signing-session",
                  content
              );

              var result = await response.Content.ReadFromJsonAsync<ApiResponse>();

              return Ok(new
              {
                  sessionToken = result.Data.Token,
                  embedUrl = result.Data.Url
              });
          }
      }`,
  };

  // ==============================================
  // CODE EXAMPLES - FRONTEND
  // ==============================================

  const frontendExamples = {
    vanilla: `<!-- HTML + Vanilla JavaScript -->
      <script src="${window.location.origin}/sdk/escribe-embed.js"></script>

      <div id="escribe-container" style="height: 700px;"></div>

      <script>
        // Initialize SDK (no API key needed on frontend!)
        eScribe.init({ baseUrl: 'https://app.escribe.io' });

        const openEmbed = async () => {
          // Get the embed URL from YOUR backend
          const { embedUrl } = await fetch('/api/get-embed-session', {
            method: 'POST'
          }).then(r => r.json());

          // Open the signing experience
          const session = await eScribe.open(embedUrl, {
            mode: 'modal',

            // Match your site's theme: 'light' | 'dark' | 'system'.
            // Update it live later with eScribe.setColorScheme('dark').
            colorScheme: 'system',

            onReady: () => {
              console.log('Signing session loaded and ready');
            },

            onComplete: (result) => {
              console.log('Document signed:', result.envelopeId);
              // Redirect or update UI
            },

            onDecline: (declineData) => {
              // Signer declined to sign — declineData may include { reason }
              console.log('Signer declined:', declineData?.reason);
            },

            onVerificationRequired: () => {
              console.log('Identity verification required before signing');
            },

            onCancel: () => {
              console.log('User cancelled');
            },

            onError: (error) => {
              console.error('Error:', error.code, error.message);
            },
          });
        };

        // Call when ready
        openEmbed();
      </script>`,

    react: `// React Component
      import { useEffect, useRef, useState } from 'react';

      const eScribeEmbed = ({ onComplete, onCancel, onDecline, onVerificationRequired }) => {
        const { t } = useTranslation();
        const containerRef = useRef(null);
        const sessionRef = useRef(null);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);

        useEffect(() => {
          let mounted = true;

          const loadEmbed = async () => {
            try {
              // Get the embed URL from your backend
              const response = await fetch('/api/get-embed-session', {
                method: 'POST',
              });
              const { embedUrl } = await response.json();

              if (!mounted || !containerRef.current) return;

              // Initialize eScribe if needed
              if (!window.eScribe._initialized) {
                window.eScribe.init({ baseUrl: 'https://app.escribe.io' });
              }

              // Open the signing session
              sessionRef.current = await window.eScribe.open(embedUrl, {
                mode: 'modal',

                // Match your app's theme: 'light' | 'dark' | 'system'.
                // Pass your current theme here, and call
                // eScribe.setColorScheme(theme) whenever the user toggles it.
                colorScheme: 'system',

                onReady: () => {
                  if (mounted) setLoading(false);
                },

                onComplete: (result) => {
                  onComplete?.(result);
                },

                onDecline: (declineData) => {
                  // Signer declined to sign — declineData may include { reason }
                  onDecline?.(declineData);
                },

                onVerificationRequired: () => {
                  // Identity verification is required before signing. The embed
                  // hides its own overlay here, so drop ours too and show it.
                  if (mounted) setLoading(false);
                  onVerificationRequired?.();
                },

                onCancel: () => {
                  onCancel?.();
                },

                onError: (err) => {
                  if (mounted) {
                    setLoading(false);
                    setError(err.message);
                  }
                },
              });
            } catch (err) {
              if (mounted) {
                setLoading(false);
                setError(err.message);
              }
            }
          };

          loadEmbed();

          return () => {
            mounted = false;
            sessionRef.current?.destroy();
          };
        }, [onComplete, onCancel, onDecline, onVerificationRequired]);

        if (error) {
          return <div className="error">Error: {error}</div>;
        }

        return (
          <div style={{ position: 'relative', height: '700px' }}>
            {loading && (
              <div className="loading-overlay">{t('common.loading')}</div>
            )}
            <div ref={containerRef} style={{ height: '100%' }} />
          </div>
        );
      };

      export default eScribeEmbed;`,

    vue: `<!-- Vue 3 Component -->
      <template>
        <div class="escribe-wrapper" style="height: 700px;">
          <div v-if="loading" class="loading">{t('common.loading')}</div>
          <div v-if="error" class="error">{{ error }}</div>
          <div ref="containerRef" style="height: 100%;"></div>
        </div>
      </template>

      <script setup>
      import { ref, onMounted, onUnmounted } from 'vue';

      const props = defineProps({
        onComplete: Function,
        onCancel: Function,
        onDecline: Function,
        onVerificationRequired: Function,
      });

      const containerRef = ref(null);
      const loading = ref(true);
      const error = ref(null);
      let session = null;

      onMounted(async () => {
        try {
          // Get the embed URL from your backend
          const response = await fetch('/api/get-embed-session', {
            method: 'POST',
          });
          const { embedUrl } = await response.json();

          // Initialize eScribe
          window.eScribe.init({ baseUrl: 'https://app.escribe.io' });

          // Open the signing session
          session = await window.eScribe.open(embedUrl, {
            mode: 'modal',

            onReady: () => {
              loading.value = false;
            },

            onComplete: (result) => {
              props.onComplete?.(result);
            },

            onDecline: (declineData) => {
              // Signer declined to sign — declineData may include { reason }
              props.onDecline?.(declineData);
            },

            onVerificationRequired: () => {
              // Identity verification is required before signing
              props.onVerificationRequired?.();
            },

            onCancel: () => {
              props.onCancel?.();
            },

            onError: (err) => {
              error.value = err.message;
            },
          });
        } catch (err) {
          error.value = err.message;
        }
      });

      onUnmounted(() => {
        session?.destroy();
      });
      </script>`,

    angular: `// Angular Component
      import { Component, OnInit, OnDestroy, ViewChild, ElementRef, Output, EventEmitter } from '@angular/core';
      import { HttpClient } from '@angular/common/http';

      declare const eScribe: any;

      @Component({
        selector: 'app-escribe-embed',
        template: \`
          <div class="escribe-wrapper" style="height: 700px;">
            <div *ngIf="loading" class="loading">{t('common.loading')}</div>
            <div *ngIf="error" class="error">{{ error }}</div>
            <div #container style="height: 100%;"></div>
          </div>
        \`
      })
      export class eScribeEmbedComponent implements OnInit, OnDestroy {
        @ViewChild('container') containerRef!: ElementRef;
        @Output() complete = new EventEmitter<any>();
        @Output() cancel = new EventEmitter<void>();
        @Output() decline = new EventEmitter<any>();
        @Output() verificationRequired = new EventEmitter<void>();

        loading = true;
        error: string | null = null;
        private session: any = null;

        constructor(private http: HttpClient) {}

        async ngOnInit() {
          try {
            // Get the embed URL from your backend
            const response: any = await this.http
              .post('/api/get-embed-session', {})
              .toPromise();

            // Initialize eScribe
            eScribe.init({ baseUrl: 'https://app.escribe.io' });

            // Open the signing session
            this.session = await eScribe.open(response.embedUrl, {
              mode: 'modal',

              onReady: () => {
                this.loading = false;
              },

              onComplete: (result: any) => {
                this.complete.emit(result);
              },

              onDecline: (declineData: any) => {
                // Signer declined to sign — declineData may include { reason }
                this.decline.emit(declineData);
              },

              onVerificationRequired: () => {
                // Identity verification is required before signing
                this.verificationRequired.emit();
              },

              onCancel: () => {
                this.cancel.emit();
              },

              onError: (err: any) => {
                this.error = err.message;
              },
            });
          } catch (err: any) {
            this.error = err.message;
          }
        }

        ngOnDestroy() {
          this.session?.destroy();
        }
      }`,

    nextjs: `// Next.js Component (App Router)
      'use client';

      import { useEffect, useRef, useState } from 'react';
      import Script from 'next/script';

      const eScribeEmbed = ({ onComplete, onCancel, onDecline, onVerificationRequired }) => {
        const containerRef = useRef(null);
        const sessionRef = useRef(null);
        const [sdkLoaded, setSdkLoaded] = useState(false);
        const [loading, setLoading] = useState(true);
        const [error, setError] = useState(null);

        useEffect(() => {
          if (!sdkLoaded || !containerRef.current) return;

          let mounted = true;

          const loadEmbed = async () => {
            try {
              // Get the embed URL from your API route
              const response = await fetch('/api/get-embed-session', {
                method: 'POST',
              });
              const { embedUrl } = await response.json();

              if (!mounted) return;

              window.eScribe.init({ baseUrl: 'https://app.escribe.io' });

              sessionRef.current = await window.eScribe.open(embedUrl, {
                mode: 'modal',
                onReady: () => mounted && setLoading(false),
                onComplete,
                onDecline,
                onVerificationRequired: (data) => {
                  // Embed hides its own overlay here — drop ours too.
                  if (mounted) setLoading(false);
                  onVerificationRequired?.(data);
                },
                onCancel,
                onError: (err) => {
                  if (mounted) {
                    setLoading(false);
                    setError(err.message);
                  }
                },
              });
            } catch (err) {
              if (mounted) {
                setLoading(false);
                setError(err.message);
              }
            }
          };

          loadEmbed();

          return () => {
            mounted = false;
            sessionRef.current?.destroy();
          };
        }, [sdkLoaded, onComplete, onCancel, onDecline, onVerificationRequired]);

        return (
          <>
            <Script
              src="${window.location.origin}/sdk/escribe-embed.js"
              onLoad={() => setSdkLoaded(true)}
            />
            <div style={{ position: 'relative', height: '700px' }}>
              {loading && <div>{t('common.loading')}</div>}
              {error && <div>Error: {error}</div>}
              <div ref={containerRef} style={{ height: '100%' }} />
            </div>
          </>
        );
      };

      export default eScribeEmbed;`,

    svelte: `<!-- Svelte Component -->
      <script>
        import { onMount, onDestroy } from 'svelte';
        import { createEventDispatcher } from 'svelte';

        const dispatch = createEventDispatcher();

        let containerRef;
        let session = null;
        let loading = true;
        let error = null;

        onMount(async () => {
          try {
            // Get the embed URL from your backend
            const response = await fetch('/api/get-embed-session', {
              method: 'POST',
            });
            const { embedUrl } = await response.json();

            // Initialize eScribe
            window.eScribe.init({ baseUrl: 'https://app.escribe.io' });

            // Open the signing session
            session = await window.eScribe.open(embedUrl, {
              mode: 'modal',

              onReady: () => {
                loading = false;
              },

              onComplete: (result) => {
                dispatch('complete', result);
              },

              onDecline: (declineData) => {
                // Signer declined to sign — declineData may include { reason }
                dispatch('decline', declineData);
              },

              onVerificationRequired: () => {
                // Identity verification is required before signing
                dispatch('verificationRequired');
              },

              onCancel: () => {
                dispatch('cancel');
              },

              onError: (err) => {
                error = err.message;
              },
            });
          } catch (err) {
            error = err.message;
          }
        });

        onDestroy(() => {
          session?.destroy();
        });
      </script>

      <div class="escribe-wrapper" style="height: 700px;">
        {#if loading}
          <div class="loading">{t('common.loading')}</div>
        {/if}
        {#if error}
          <div class="error">{error}</div>
        {/if}
        <div bind:this={containerRef} style="height: 100%;"></div>
      </div>`,
  };

  const renderSessionContent = () => {
    if (sessionsLoading) {
      return (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      );
    }
    if (sessionsError) {
      return (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>{t("errors.loadingSessions")}</AlertTitle>
          <AlertDescription>{sessionsError}</AlertDescription>
        </Alert>
      );
    }

    return sessions.length === 0 ? (
      <div className="text-center py-8 text-muted-foreground">
        <Activity className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>{t("embed.sessions.empty")}</p>
        <p className="text-sm">{t("embed.sessions.emptyDesc")}</p>
      </div>
    ) : (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("embed.sessions.token")}</TableHead>
            <TableHead>{t("embed.sessions.type")}</TableHead>
            <TableHead>{t("embed.sessions.status")}</TableHead>
            <TableHead>{t("embed.sessions.origin")}</TableHead>
            <TableHead>{t("embed.sessions.created")}</TableHead>
            <TableHead>{t("embed.sessions.expires")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {sessions.map((session) => (
            <TableRow key={session.id}>
              <TableCell className="font-mono text-xs">
                {session.token?.substring(0, 16)}...
              </TableCell>
              <TableCell>
                <Badge variant="outline">{session.type}</Badge>
              </TableCell>
              <TableCell>
                <SessionStatusBadge status={session.status} />
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {session.origin_domain || "-"}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {new Date(session.created_at).toLocaleString()}
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {new Date(session.expires_at).toLocaleString()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  };

  // Combined example: brand colors (set server-side at session creation, so the
  // iframe URL can't tamper with them) + light/dark scheme (set client-side and
  // toggleable live). The two are independent and complementary.
  const combinedThemingExample = `// 1) BACKEND — set your brand colors when you create the session.
//    Stored with the session; applies in both light and dark mode.
const res = await fetch('https://api.escribe.io/api/v1/embed/signing-session', {
  method: 'POST',
  headers: {
    'X-API-Key': process.env.ESCRIBE_API_KEY,
    'Content-Type': 'application/json',
  },
  body: JSON.stringify({
    envelopeId,
    recipientId,
    theme: {
      primaryColor: '#7c3aed',
      companyName: 'Acme Inc.',
      logoUrl: 'https://acme.com/logo.png',
    },
  }),
});
const { data } = await res.json(); // { url, token, ... }

// 2) FRONTEND — pass your current light/dark scheme when opening…
eScribe.init({ baseUrl: 'https://app.escribe.io' });
const isDark = document.documentElement.classList.contains('dark');
const session = await eScribe.open(data.url, {
  mode: 'modal',
  colorScheme: isDark ? 'dark' : 'light', // 'light' | 'dark' | 'system'
});

// …then keep it in sync when your user toggles their theme.
themeToggle.addEventListener('change', (e) => {
  eScribe.setColorScheme(e.target.checked ? 'dark' : 'light');
});`;

  const renderThemeContent = () => {
    if (themeLoading) {
      return (
        <Card>
          <CardContent className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-8 w-48" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </CardContent>
        </Card>
      );
    }
    return themeError ? (
      <Alert variant="destructive">
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>{t("errors.loadingTheme")}</AlertTitle>
        <AlertDescription>{themeError}</AlertDescription>
      </Alert>
    ) : (
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Theme Editor */}
        <Card>
          <CardHeader>
            <CardTitle>{t("embed.theme.editor")}</CardTitle>
            <CardDescription>{t("embed.theme.editorDesc")}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Branding */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Image className="h-4 w-4" />
                {t("embed.theme.branding")}
              </h4>

              <div className="space-y-2">
                <Label>{t("embed.theme.companyName")}</Label>
                <Input
                  value={themeForm.companyName}
                  onChange={(e) =>
                    setThemeForm({
                      ...themeForm,
                      companyName: e.target.value,
                    })
                  }
                  placeholder={t("placeholders.yourCompany")}
                />
              </div>

              <div className="space-y-2">
                <Label>{t("embed.theme.logoUrl")}</Label>
                <Input
                  value={themeForm.logoUrl}
                  onChange={(e) =>
                    setThemeForm({
                      ...themeForm,
                      logoUrl: e.target.value,
                    })
                  }
                  placeholder="https://example.com/logo.png"
                />
              </div>

              <div className="space-y-2">
                <Label>{t("embed.theme.faviconUrl")}</Label>
                <Input
                  value={themeForm.faviconUrl}
                  onChange={(e) =>
                    setThemeForm({
                      ...themeForm,
                      faviconUrl: e.target.value,
                    })
                  }
                  placeholder="https://example.com/favicon.ico"
                />
              </div>
            </div>

            <Separator />

            {/* Colors */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Palette className="h-4 w-4" />
                {t("embed.theme.colors")}
              </h4>

              <div className="grid gap-4 sm:grid-cols-2">
                <ColorPicker
                  label={t("embed.theme.primaryColor")}
                  value={themeForm.primaryColor}
                  onChange={(v) =>
                    setThemeForm({ ...themeForm, primaryColor: v })
                  }
                  id="color-primary"
                />
                <ColorPicker
                  label={t("embed.theme.backgroundColor")}
                  value={themeForm.backgroundColor}
                  onChange={(v) =>
                    setThemeForm({ ...themeForm, backgroundColor: v })
                  }
                  id="color-background"
                />
                <ColorPicker
                  label={t("embed.theme.textColor")}
                  value={themeForm.textColor}
                  onChange={(v) => setThemeForm({ ...themeForm, textColor: v })}
                  id="color-text"
                />
                <ColorPicker
                  label={t("embed.theme.buttonTextColor")}
                  value={themeForm.buttonTextColor}
                  onChange={(v) =>
                    setThemeForm({ ...themeForm, buttonTextColor: v })
                  }
                  id="color-button-text"
                />
              </div>
            </div>

            <Separator />

            {/* Typography */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Type className="h-4 w-4" />
                {t("embed.theme.typography")}
              </h4>

              <div className="space-y-2">
                <Label>{t("embed.theme.fontFamily")}</Label>
                <Select
                  value={themeForm.fontFamily}
                  onValueChange={(v) =>
                    setThemeForm({ ...themeForm, fontFamily: v })
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Inter, sans-serif">
                      {t("fonts.inter")}
                    </SelectItem>
                    <SelectItem value="Roboto, sans-serif">
                      {t("fonts.roboto")}
                    </SelectItem>
                    <SelectItem value="Open Sans, sans-serif">
                      {t("fonts.openSans")}
                    </SelectItem>
                    <SelectItem value="Lato, sans-serif">
                      {t("fonts.lato")}
                    </SelectItem>
                    <SelectItem value="Poppins, sans-serif">
                      {t("fonts.poppins")}
                    </SelectItem>
                    <SelectItem value="system-ui, sans-serif">
                      {t("settings.systemDefault")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>{t("embed.theme.fontUrl")}</Label>
                <Input
                  value={themeForm.fontUrl}
                  onChange={(e) =>
                    setThemeForm({
                      ...themeForm,
                      fontUrl: e.target.value,
                    })
                  }
                  placeholder="https://fonts.googleapis.com/css2?family=..."
                />
              </div>
            </div>

            <Separator />

            {/* Premium Options */}
            <div className="space-y-4">
              <h4 className="font-medium flex items-center gap-2">
                <Settings className="h-4 w-4" />
                {t("embed.theme.advanced")}
              </h4>

              <div className="flex items-center justify-between">
                <div>
                  <Label>{t("embed.theme.hideBranding")}</Label>
                  <p className="text-sm text-muted-foreground">
                    {t("embed.theme.hideBrandingDesc")}
                  </p>
                </div>
                <Switch
                  checked={themeForm.hideeScribeBranding}
                  onCheckedChange={(v) =>
                    setThemeForm({
                      ...themeForm,
                      hideeScribeBranding: v,
                    })
                  }
                  disabled={!whiteLabelEnabled}
                />
              </div>

              {!whiteLabelEnabled && (
                <p className="text-sm text-amber-600">
                  {t("embed.premium.whiteLabelRequired")}
                </p>
              )}

              <div className="space-y-2">
                <Label>{t("embed.theme.allowedDomains")}</Label>
                <Input
                  value={themeForm.allowedDomains}
                  onChange={(e) =>
                    setThemeForm({
                      ...themeForm,
                      allowedDomains: e.target.value,
                    })
                  }
                  placeholder="example.com, *.myapp.com"
                />
                <p className="text-xs text-muted-foreground">
                  {t("embed.theme.allowedDomainsDesc")}
                </p>
              </div>

              <div className="space-y-2">
                <Label>{t("embed.theme.customCss")}</Label>
                <Textarea
                  value={themeForm.customCss}
                  onChange={(e) =>
                    setThemeForm({
                      ...themeForm,
                      customCss: e.target.value,
                    })
                  }
                  placeholder=".escribe-container { ... }"
                  rows={4}
                  disabled={!customCssEnabled}
                />
                {!customCssEnabled && (
                  <p className="text-sm text-amber-600">
                    {t("embed.premium.customCssRequired")}
                  </p>
                )}
              </div>
            </div>

            <div className="pt-4">
              <Button
                onClick={handleSaveTheme}
                disabled={themeSaving}
                className="w-full"
              >
                {themeSaving && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {theme?.id ? t("common.update") : t("common.create")}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Preview */}
        <div className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>{t("embed.theme.preview")}</CardTitle>
              <CardDescription>{t("embed.theme.previewDesc")}</CardDescription>
            </CardHeader>
            <CardContent>
              <ThemePreview theme={themeForm} />
            </CardContent>
          </Card>

          {/* Combined theming example: brand colors + light/dark scheme */}
          <Card>
            <CardHeader>
              <CardTitle>
                {t("embed.theme.combinedExample", "Brand colors + dark mode")}
              </CardTitle>
              <CardDescription>
                {t(
                  "embed.theme.combinedExampleDesc",
                  "Brand colors are set server-side when you create the session; light/dark is passed from the browser and can change live.",
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CodeBlock
                code={combinedThemingExample}
                language="javascript"
                onCopy={() =>
                  copyCode(combinedThemingExample, "combined-theming")
                }
                copied={copiedCode === "combined-theming"}
              />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  };

  // ============================================
  // Feature Check - Show upgrade message if embed not available
  // ============================================

  if (!hasEmbedFeature) {
    return <UpgradeRequired />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-heading tracking-tight">
            {t("embed.title")}
          </h1>
          <p className="text-muted-foreground">{t("embed.description")}</p>
        </div>

        <div className="flex items-center gap-4">
          {embedEnabled ? (
            <Badge className="bg-emerald-500 text-white">
              {t("common.enabled")}
            </Badge>
          ) : (
            <Badge
              variant="outline"
              className="text-amber-600 border-amber-300"
            >
              {t("embed.notEnabled")}
            </Badge>
          )}
          <Button variant="outline" asChild>
            <a href="/embed-sdk.html" target="_blank" className="gap-2">
              <ExternalLink className="h-4 w-4" />
              {t("embed.viewDocs")}
            </a>
          </Button>
        </div>
      </div>

      {/* Feature not enabled alert */}
      {!embedEnabled && (
        <div className="p-4 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <div className="flex gap-3">
            <div className="shrink-0">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-amber-500/10">
                <AlertCircle className="w-4 h-4 text-amber-500" />
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                {t("embed.enableRequired")}
              </p>
              <p className="text-sm text-muted-foreground leading-relaxed">
                {t("embed.enableDescription")}
              </p>
              {/* <code className="block mt-2 p-2 bg-muted rounded text-xs">
                UPDATE users SET embed_enabled = true WHERE id = '{user?.id}';
              </code> */}
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-card rounded-xl shadow-sm border">
        <div className="border-b">
          <nav className="flex -mb-px">
            <button
              onClick={() => setActiveTab("quickstart")}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors cursor-pointer ${
                activeTab === "quickstart"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Code className="w-4 h-4" />
              Quick Start
            </button>
            <button
              onClick={() => setActiveTab("theme")}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors cursor-pointer ${
                activeTab === "theme"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Palette className="w-4 h-4" />
              Theme
            </button>
            <button
              onClick={() => setActiveTab("sessions")}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors cursor-pointer ${
                activeTab === "sessions"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Activity className="w-4 h-4" />
              Sessions
            </button>
            <button
              onClick={() => setActiveTab("test")}
              className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm transition-colors cursor-pointer ${
                activeTab === "test"
                  ? "border-primary text-primary"
                  : "border-transparent text-muted-foreground hover:text-foreground"
              }`}
            >
              <Play className="w-4 h-4" />
              Quick Test
            </button>
          </nav>
        </div>

        <div className="p-6 space-y-6">
          {/* Quick Start Tab */}
          {activeTab === "quickstart" && (
            <>
              {/* SDK Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Code className="h-5 w-5" />
                    {t("embed.sdk.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("embed.sdk.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-2 p-3 bg-muted rounded-lg font-mono text-sm">
                    <code className="flex-1 overflow-x-auto">{`<script src="${window.location.origin}/sdk/escribe-embed.js"></script>`}</code>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() =>
                        copyCode(
                          `<script src="${window.location.origin}/sdk/escribe-embed.js"></script>`,
                          "sdk",
                        )
                      }
                    >
                      {copiedCode === "sdk" ? (
                        <Check className="h-4 w-4" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                  </div>

                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline">v2.0.0</Badge>
                    <Badge variant="outline">
                      {t("embed.typescriptSupport")}
                    </Badge>
                    <Badge variant="outline">~10KB gzipped</Badge>
                    <Badge variant="outline">
                      {t("embed.esModuleAvailable")}
                    </Badge>
                  </div>
                </CardContent>
              </Card>

              {/* Backend Examples */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    1. {t("embed.steps.backend")}
                  </CardTitle>
                  <CardDescription>
                    {t("embed.steps.backendDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2 flex-wrap">
                    {Object.keys(backendExamples).map((lang) => (
                      <Button
                        key={lang}
                        variant={
                          selectedBackendLang === lang ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSelectedBackendLang(lang)}
                      >
                        {getCurrentBackend()[lang]}
                      </Button>
                    ))}
                  </div>

                  <CodeBlock
                    code={backendExamples[selectedBackendLang]}
                    language={selectedBackendLang}
                    onCopy={() =>
                      copyCode(
                        backendExamples[selectedBackendLang],
                        `backend-${selectedBackendLang}`,
                      )
                    }
                    copied={copiedCode === `backend-${selectedBackendLang}`}
                  />
                </CardContent>
              </Card>

              {/* Frontend Examples */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">
                    2. {t("embed.steps.frontend")}
                  </CardTitle>
                  <CardDescription>
                    {t("embed.steps.frontendDesc")}
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex gap-2 flex-wrap">
                    {Object.keys(frontendExamples).map((lang) => (
                      <Button
                        key={lang}
                        variant={
                          selectedFrontendLang === lang ? "default" : "outline"
                        }
                        size="sm"
                        onClick={() => setSelectedFrontendLang(lang)}
                      >
                        {getCurrentFrontend()[lang]}
                      </Button>
                    ))}
                  </div>

                  <CodeBlock
                    code={frontendExamples[selectedFrontendLang]}
                    language={selectedFrontendLang}
                    onCopy={() =>
                      copyCode(
                        frontendExamples[selectedFrontendLang],
                        `frontend-${selectedFrontendLang}`,
                      )
                    }
                    copied={copiedCode === `frontend-${selectedFrontendLang}`}
                  />
                </CardContent>
              </Card>

              {/* Webhooks pointer */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Webhook className="h-5 w-5" />
                    {t("embed.webhooksPointer.title")}
                  </CardTitle>
                  <CardDescription>
                    {t("embed.webhooksPointer.description")}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" onClick={() => navigate("/webhooks")}>
                      {t("embed.webhooksPointer.manage")}
                    </Button>
                    <Button size="sm" variant="outline" asChild>
                      <a
                        href="/embed-sdk.html#webhooks"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t("embed.webhooksPointer.verifyGuide")}
                        <ExternalLink className="ml-2 h-3.5 w-3.5" />
                      </a>
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Security Note */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>{t("admin.securityBestPractices")}</AlertTitle>
                <AlertDescription>
                  <ul className="list-disc list-inside mt-2 space-y-1 text-sm">
                    <li>{t("apiKeys.neverExposeWarning")}</li>
                    <li>{t("embed.alwaysCreateFromBackend")}</li>
                    <li>{t("embed.sessionTokensShortLived")}</li>
                    <li>{t("embed.configureAllowedDomains")}</li>
                    <li>{t("embed.useHttps")}</li>
                  </ul>
                </AlertDescription>
              </Alert>
            </>
          )}

          {/* Theme Tab */}
          {activeTab === "theme" && <>{renderThemeContent()}</>}

          {/* Sessions Tab */}
          {activeTab === "sessions" && (
            <>
              {/* Stats */}
              <div className="grid gap-4 md:grid-cols-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("embed.stats.total")}
                        </p>
                        <p className="text-2xl font-bold">
                          {sessionStats.total}
                        </p>
                      </div>
                      <Activity className="h-8 w-8 text-muted-foreground" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("embed.stats.active")}
                        </p>
                        <p className="text-2xl font-bold text-blue-600">
                          {sessionStats.active || 0}
                        </p>
                      </div>
                      <Clock className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("embed.stats.completed")}
                        </p>
                        <p className="text-2xl font-bold text-emerald-600">
                          {sessionStats.completed || 0}
                        </p>
                      </div>
                      <CheckCircle className="h-8 w-8 text-emerald-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">
                          {t("embed.stats.expired")}
                        </p>
                        <p className="text-2xl font-bold text-gray-600 dark:text-gray-400">
                          {sessionStats.expired || 0}
                        </p>
                      </div>
                      <XCircle className="h-8 w-8 text-gray-600 dark:text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Sessions Table */}
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle>{t("embed.sessions.recent")}</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-2"
                      onClick={refreshSessions}
                      disabled={sessionsRefetching}
                    >
                      <RefreshCw
                        className={cn(
                          "h-4 w-4",
                          sessionsRefetching && "animate-spin",
                        )}
                      />
                      {t("common.refresh")}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>{renderSessionContent()}</CardContent>
              </Card>
            </>
          )}

          {/* Quick Test Tab */}
          {activeTab === "test" && (
            <>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Play className="h-5 w-5" />
                    Test Embed Signing
                  </CardTitle>
                  <CardDescription>
                    Select a sent envelope and recipient to test the embed
                    signing experience
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Step 1: Select Envelope */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Step 1: Select Envelope
                    </Label>
                    <Select
                      value={selectedEnvelopeId}
                      onValueChange={handleEnvelopeChange}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={renderTestEnvelopesPlaceholder()}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {testEnvelopes.map((envelope) => (
                          <SelectItem key={envelope.id} value={envelope.id}>
                            {envelope.title} - {envelope.recipientCount || 0}{" "}
                            recipient(s)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {testEnvelopes.length === 0 && !testEnvelopesLoading && (
                      <p className="text-sm text-muted-foreground">
                        You need to have at least one envelope with "sent"
                        status.
                        <a
                          href="/envelopes"
                          className="text-primary hover:underline ml-1"
                        >
                          Create and send an envelope first
                        </a>
                        .
                      </p>
                    )}
                  </div>

                  {/* Step 2: Select Recipient */}
                  {selectedEnvelopeId && (
                    <div className="space-y-2">
                      <Label className="flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Step 2: Select Recipient
                        {selectedEnvelopeLoading && (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        )}
                      </Label>
                      <Select
                        value={selectedRecipientId}
                        onValueChange={setSelectedRecipientId}
                        disabled={selectedEnvelopeLoading}
                      >
                        <SelectTrigger>
                          <SelectValue
                            placeholder={renderAvailableParticipantsPlaceholder()}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          {availableRecipients.map((recipient) => (
                            <SelectItem key={recipient.id} value={recipient.id}>
                              {recipient.name} ({recipient.email}) -{" "}
                              {recipient.status}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!selectedEnvelopeLoading &&
                        availableRecipients.length === 0 &&
                        selectedEnvelopeData && (
                          <p className="text-sm text-muted-foreground">
                            All signers have already completed signing this
                            envelope.
                          </p>
                        )}
                    </div>
                  )}

                  {/* Step 3: Generate Session */}
                  {selectedRecipientId && (
                    <div className="space-y-4">
                      <Separator />
                      <div className="flex items-center gap-3">
                        <Button
                          onClick={createTestSession}
                          disabled={testLoading}
                          className="gap-2"
                        >
                          {testLoading ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin" />
                              Creating Session...
                            </>
                          ) : (
                            <>
                              <Play className="h-4 w-4" />
                              Generate Test Session
                            </>
                          )}
                        </Button>
                        {testEmbedUrl && (
                          <Button
                            variant="outline"
                            onClick={() => window.open(testEmbedUrl, "_blank")}
                            className="gap-2"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Open in New Tab
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Embed URL */}
                  {testEmbedUrl && (
                    <div className="space-y-2">
                      <Label>Embed URL</Label>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 p-3 bg-muted rounded-lg text-xs break-all">
                          {testEmbedUrl}
                        </code>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            navigator.clipboard.writeText(testEmbedUrl);
                            toast.success("URL copied to clipboard");
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Embed Preview */}
              {testEmbedUrl && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Activity className="h-5 w-5" />
                      Live Preview
                    </CardTitle>
                    <CardDescription>
                      The signing interface is loaded below. Complete the
                      signing to test the full flow.
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="border rounded-lg overflow-hidden bg-white">
                      <iframe
                        src={testEmbedUrl}
                        className="w-full h-175"
                        title="Embed Signing Preview"
                        allow="camera; microphone"
                      />
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Help */}
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertTitle>How to Test</AlertTitle>
                <AlertDescription>
                  <ol className="list-decimal list-inside mt-2 space-y-1 text-sm">
                    <li>
                      Create an envelope with a PDF document and signature
                      fields
                    </li>
                    <li>Send the envelope to make it available for signing</li>
                    <li>
                      Come back here and select the envelope and recipient
                    </li>
                    <li>
                      Generate a test session and complete the signing in the
                      preview
                    </li>
                  </ol>
                </AlertDescription>
              </Alert>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default Embed;
